import { useEffect, useState } from "react";
import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";

function FloatingOrbs() {
  return (
    <>
      <div className="absolute top-[5%] left-[20%] w-64 h-64 rounded-full bg-white/[0.03] blur-3xl animate-float-2" />
      <div className="absolute bottom-[30%] right-[10%] w-72 h-72 rounded-full bg-silver-300/[0.03] blur-3xl animate-float-1" />
      <div className="absolute top-[50%] left-[5%] w-52 h-52 rounded-full bg-white/[0.02] blur-2xl animate-float-3" />
    </>
  );
}

function TechScanLine() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.04]">
      <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent animate-scan-line" />
    </div>
  );
}

function TechParticles() {
  const particles = [
    { left: "10%", top: "28%", delay: "0s", size: 2 },
    { left: "24%", top: "54%", delay: "1.6s", size: 3 },
    { left: "42%", top: "36%", delay: "2.0s", size: 2 },
    { left: "56%", top: "66%", delay: "0.8s", size: 1.5 },
    { left: "70%", top: "22%", delay: "1.4s", size: 2.5 },
    { left: "84%", top: "44%", delay: "2.2s", size: 2 },
    { left: "16%", top: "76%", delay: "2.8s", size: 1.5 },
    { left: "64%", top: "84%", delay: "1.0s", size: 2 },
    { left: "78%", top: "30%", delay: "2.6s", size: 3 },
    { left: "34%", top: "88%", delay: "0.5s", size: 1.5 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none hidden md:block">
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white/60 animate-tech-particle"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
            animationDuration: `${3 + Math.random() * 3}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function ResourcesHero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative bg-navy-950 overflow-hidden">
      <FloatingOrbs />
      <TechScanLine />
      <TechParticles />

      <div className="absolute inset-0">
        <SmartImage
          src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20knowledge%20graph%20and%20digital%20archive%20visualization%20with%20floating%20holographic%20documents%20and%20network%20nodes%20on%20dark%20background%2C%20geometric%20constellation%20of%20interconnected%20data%20points%20in%20silver%20white%20and%20soft%20warm%20tones%2C%20deep%20navy%20black%20base%2C%20high%20contrast%20minimalist%20technology%20art%2C%20professional%20corporate%20aesthetic%2C%20elegant%20composition%20without%20people&width=1400&height=700&seq=taiaa-resources-hero&orientation=landscape"
          alt="資源中心"
          className="w-full h-full object-cover opacity-40"
          hideOnError
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/85 via-navy-950/65 to-navy-950/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/40" />
      </div>

      <div className="absolute inset-0 pointer-events-none opacity-[0.08]">
        <div
          className="w-full h-full animate-grid-move"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "100px 100px",
          }}
        />
      </div>

      <div className="relative z-10 px-6 md:px-10 lg:px-16 pt-32 pb-10 md:pt-40 md:pb-14">
        <div className="max-w-4xl">
          <p
            className={`text-sm font-medium tracking-[0.2em] text-silver-400 uppercase mb-4 transition-all duration-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Resources Center
          </p>
          <h1
            className={`font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6 transition-all duration-700 delay-150 animate-text-glow ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            資源中心
          </h1>
          <p
            className={`text-silver-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8 transition-all duration-700 delay-300 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            TAIAA 致力於彙整與產出高品質 AI 產業研究、實務指南與政策分析。在此下載協會出版品、瀏覽產業報告，並探索相關法規與實用連結。
          </p>

          <div
            className={`flex flex-wrap gap-4 transition-all duration-700 delay-[450ms] ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <a
              href="#publications"
              className="inline-flex items-center gap-2 bg-white text-navy-950 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-silver-100 transition-colors whitespace-nowrap"
            >
              出版品下載
              <Icon className="ri-arrow-down-line" />
            </a>
            <a
              href="#links"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              相關連結
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}